import { useState, useEffect } from 'react';
import { X, Save } from 'lucide-react';
import toast from 'react-hot-toast';
import { lazyLogger } from '../../services/lazyLogger';

interface IDESettings {
  fontSize: number;
  theme: 'vs-dark' | 'vs' | 'hc-black';
  wordWrap: 'on' | 'off';
  tabSize: number;
  minimapEnabled: boolean;
  lineNumbers: 'on' | 'off' | 'relative';
  formatOnSave: boolean;
  autoSave: boolean;
}

const defaultSettings: IDESettings = {
  fontSize: 14,
  theme: 'vs-dark',
  wordWrap: 'on',
  tabSize: 2,
  minimapEnabled: true,
  lineNumbers: 'on',
  formatOnSave: true,
  autoSave: true,
};

interface IDESettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (settings: IDESettings) => void;
}

export function IDESettingsModal({ isOpen, onClose, onSave }: IDESettingsModalProps) {
  const [settings, setSettings] = useState<IDESettings>(defaultSettings);

  useEffect(() => {
    if (isOpen) {
      // Load saved settings from localStorage
      const saved = localStorage.getItem('ide_settings');
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          setSettings({ ...defaultSettings, ...parsed });
        } catch (error) {
          lazyLogger.error('Failed to load IDE settings', { error });
        }
      }
    }
  }, [isOpen]);

  const handleSave = () => {
    localStorage.setItem('ide_settings', JSON.stringify(settings));
    onSave(settings);
    toast.success('IDE settings saved');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className='modal'>
      <div className='modal-content max-w-2xl max-h-[90vh] flex flex-col'>
        {/* Header */}
        <div className='flex items-center justify-between p-6 border-b border-default'>
          <h2 className='text-xl font-semibold text-primary'>
            IDE Settings
          </h2>
          <button
            onClick={onClose}
            className='p-2 text-muted hover:text-secondary hover:bg-subtle rounded-lg transition-colors'
            aria-label='Close settings'
          >
            <X className='w-5 h-5' />
          </button>
        </div>

        {/* Content */}
        <div className='flex-1 overflow-y-auto p-6 space-y-6'>
          {/* Font Size */}
          <div>
            <label className='block text-sm font-medium text-primary mb-2'>
              Font Size: {settings.fontSize}px
            </label>
            <input
              type='range'
              min='10'
              max='24'
              value={settings.fontSize}
              onChange={(e) =>
                setSettings({ ...settings, fontSize: parseInt(e.target.value) })
              }
              className='w-full h-2 bg-subtle rounded-lg appearance-none cursor-pointer'
            />
            <div className='flex justify-between text-xs text-muted mt-1'>
              <span>10px</span>
              <span>24px</span>
            </div>
          </div>

          {/* Theme */}
          <div>
            <label className='block text-sm font-medium text-primary mb-2'>
              Editor Theme
            </label>
            <select
              value={settings.theme}
              onChange={(e) =>
                setSettings({
                  ...settings,
                  theme: e.target.value as IDESettings['theme'],
                })
              }
              className='input w-full'
            >
              <option value='vs-dark'>Dark</option>
              <option value='vs'>Light</option>
              <option value='hc-black'>High Contrast</option>
            </select>
          </div>

          {/* Tab Size */}
          <div>
            <label className='block text-sm font-medium text-primary mb-2'>
              Tab Size: {settings.tabSize} spaces
            </label>
            <input
              type='range'
              min='2'
              max='8'
              value={settings.tabSize}
              onChange={(e) =>
                setSettings({ ...settings, tabSize: parseInt(e.target.value) })
              }
              className='w-full h-2 bg-subtle rounded-lg appearance-none cursor-pointer'
            />
            <div className='flex justify-between text-xs text-muted mt-1'>
              <span>2</span>
              <span>8</span>
            </div>
          </div>

          {/* Word Wrap */}
          <div>
            <label className='flex items-center gap-3'>
              <input
                type='checkbox'
                checked={settings.wordWrap === 'on'}
                onChange={(e) =>
                  setSettings({
                    ...settings,
                    wordWrap: e.target.checked ? 'on' : 'off',
                  })
                }
                className='w-4 h-4 text-accent-500 border-default rounded focus:ring-accent-500'
              />
              <span className='text-sm font-medium text-primary'>
                Word Wrap
              </span>
            </label>
          </div>

          {/* Minimap */}
          <div>
            <label className='flex items-center gap-3'>
              <input
                type='checkbox'
                checked={settings.minimapEnabled}
                onChange={(e) =>
                  setSettings({ ...settings, minimapEnabled: e.target.checked })
                }
                className='w-4 h-4 text-accent-500 border-default rounded focus:ring-accent-500'
              />
              <span className='text-sm font-medium text-primary'>
                Show Minimap
              </span>
            </label>
          </div>

          {/* Line Numbers */}
          <div>
            <label className='block text-sm font-medium text-primary mb-2'>
              Line Numbers
            </label>
            <select
              value={settings.lineNumbers}
              onChange={(e) =>
                setSettings({
                  ...settings,
                  lineNumbers: e.target.value as IDESettings['lineNumbers'],
                })
              }
              className='input w-full'
            >
              <option value='on'>On</option>
              <option value='off'>Off</option>
              <option value='relative'>Relative</option>
            </select>
          </div>

          {/* Format on Save */}
          <div>
            <label className='flex items-center gap-3'>
              <input
                type='checkbox'
                checked={settings.formatOnSave}
                onChange={(e) =>
                  setSettings({ ...settings, formatOnSave: e.target.checked })
                }
                className='w-4 h-4 text-accent-500 border-default rounded focus:ring-accent-500'
              />
              <span className='text-sm font-medium text-primary'>
                Format on Save
              </span>
            </label>
          </div>

          {/* Auto Save */}
          <div>
            <label className='flex items-center gap-3'>
              <input
                type='checkbox'
                checked={settings.autoSave}
                onChange={(e) =>
                  setSettings({ ...settings, autoSave: e.target.checked })
                }
                className='w-4 h-4 text-accent-500 border-default rounded focus:ring-accent-500'
              />
              <span className='text-sm font-medium text-primary'>
                Auto Save
              </span>
            </label>
          </div>
        </div>

        {/* Footer */}
        <div className='flex items-center justify-end gap-3 p-6 border-t border-default'>
          <button
            onClick={onClose}
            className='btn btn-outline'
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className='btn btn-primary'
          >
            <Save className='w-4 h-4' />
            Save Settings
          </button>
        </div>
      </div>
    </div>
  );
}

